// Audio utility for GuapGoals
// Provides sound effects for transactions and achievements

// Sound effect URLs (royalty-free from various sources)
const SOUNDS = {
  // Cash register / cha-ching sounds
  cashRegister: 'https://assets.mixkit.co/active_storage/sfx/2058/2058-preview.mp3',
  coinDrop: 'https://assets.mixkit.co/active_storage/sfx/2003/2003-preview.mp3',
  success: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3',
  levelUp: 'https://assets.mixkit.co/active_storage/sfx/2020/2020-preview.mp3',
  achievement: 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3',
  notification: 'https://assets.mixkit.co/active_storage/sfx/2017/2017-preview.mp3',
  // Custom celebration sounds
  dababyYeah: 'https://customer-assets.emergentagent.com/job_money-manager-pro-4/artifacts/nfnxtgm5_DaBaby%20Yeah%20Yeah%20%28Adlib%20Meme%29%20-%20Sound%20Effect%20for%20editing.m4a',
  lilJonYeah: 'https://customer-assets.emergentagent.com/job_money-manager-pro-4/artifacts/3ejl0ogg_Lil%20Jon%20Yeah%20-%20Sound%20Effect%20for%20Editing.m4a',
};

// Available celebration sound options for user preference
export const CELEBRATION_SOUND_OPTIONS = [
  { id: 'default', name: 'Default (Cash Register)', sound: 'cashRegister' },
  { id: 'dababy', name: 'DaBaby "Yeah Yeah"', sound: 'dababyYeah' },
  { id: 'liljon', name: 'Lil Jon "Yeah!"', sound: 'lilJonYeah' },
];

// Audio cache to prevent multiple loads
const audioCache = {};

// Check if audio is enabled (user preference)
const isAudioEnabled = () => {
  const preference = localStorage.getItem('guapgoals_audio_enabled');
  return preference === null || preference === 'true';
};

// Set audio preference
export const setAudioEnabled = (enabled) => {
  localStorage.setItem('guapgoals_audio_enabled', String(enabled));
};

// Get audio enabled state
export const getAudioEnabled = () => isAudioEnabled();

// Get current celebration sound preference
export const getCelebrationSound = () => {
  const preference = localStorage.getItem('guapgoals_celebration_sound');
  return preference || 'default';
};

// Set celebration sound preference
export const setCelebrationSound = (soundId) => {
  localStorage.setItem('guapgoals_celebration_sound', soundId);
};

// Preload a sound
const preloadSound = (soundKey) => {
  if (!audioCache[soundKey] && SOUNDS[soundKey]) {
    const audio = new Audio(SOUNDS[soundKey]);
    audio.preload = 'auto';
    audio.volume = 0.5;
    audioCache[soundKey] = audio;
  }
  return audioCache[soundKey];
};

// Play a sound effect
export const playSound = (soundKey, volume = 0.5) => {
  if (!isAudioEnabled()) return;
  
  try {
    let audio = audioCache[soundKey];
    
    if (!audio && SOUNDS[soundKey]) {
      audio = new Audio(SOUNDS[soundKey]);
      audioCache[soundKey] = audio;
    }
    
    if (audio) {
      audio.volume = Math.min(1, Math.max(0, volume));
      audio.currentTime = 0;
      audio.play().catch(err => {
        // Silently fail - browser may block autoplay
        console.log('Audio playback blocked:', err.message);
      });
    }
  } catch (error) {
    console.log('Audio error:', error.message);
  }
};

// Specific sound functions for easy use
export const playCashRegister = () => playSound('cashRegister', 0.4);
export const playCoinDrop = () => playSound('coinDrop', 0.5);
export const playSuccess = () => playSound('success', 0.5);
export const playLevelUp = () => playSound('levelUp', 0.6);
export const playAchievement = () => playSound('achievement', 0.6);
export const playNotification = () => playSound('notification', 0.3);

// Play the user's chosen celebration sound
export const playCelebration = () => {
  const soundPref = getCelebrationSound();
  const soundOption = CELEBRATION_SOUND_OPTIONS.find(opt => opt.id === soundPref);
  const soundKey = soundOption ? soundOption.sound : 'cashRegister';
  playSound(soundKey, 0.6);
};

// Preload common sounds on module load
if (typeof window !== 'undefined') {
  ['cashRegister', 'success', 'achievement', 'dababyYeah', 'lilJonYeah'].forEach(preloadSound);
}

export default {
  playSound,
  playCashRegister,
  playCoinDrop,
  playSuccess,
  playLevelUp,
  playAchievement,
  playNotification,
  playCelebration,
  setAudioEnabled,
  getAudioEnabled,
  getCelebrationSound,
  setCelebrationSound,
  CELEBRATION_SOUND_OPTIONS,
};
